/**
 * DocumentationPanel Component - Shows documents linked to an issue via document_relations
 * Uses the document-service's by-entity endpoint instead of work-service documentation system.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi, type EditorDocument } from '../../../api/documents';
import { DocumentationBrowser } from './DocumentationBrowser';
import { useNavigate } from 'react-router-dom';
import { useProject, getProjectShortId } from '../../../contexts/ProjectContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { FileText, BookOpen, StickyNote, Layout, X } from 'lucide-react';

interface DocumentationPanelProps {
  projectId: string;
  issueId: string;
}

const DOC_TYPE_LABELS: Record<string, { label: string; Icon: typeof FileText }> = {
  spec: { label: 'Spec', Icon: BookOpen },
  guide: { label: 'Guide', Icon: Layout },
  note: { label: 'Note', Icon: StickyNote },
  document: { label: 'Document', Icon: FileText },
  template: { label: 'Template', Icon: FileText },
  test: { label: 'Test', Icon: FileText },
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-neutral-100 text-neutral-500',
  in_progress: 'bg-blue-50 text-blue-600',
  review: 'bg-amber-50 text-amber-600',
  published: 'bg-emerald-50 text-emerald-600',
  archived: 'bg-neutral-100 text-neutral-400',
};

export function DocumentationPanel({ projectId, issueId }: DocumentationPanelProps) {
  const [showBrowser, setShowBrowser] = useState(false);
  const navigate = useNavigate();
  const { wsSlug } = useWorkspace();
  const queryClient = useQueryClient();

  // Fetch documents linked to this issue
  const { data, isLoading } = useQuery({
    queryKey: ['docs-by-entity', projectId, 'issue', issueId],
    queryFn: () => documentsApi.links.byEntity(projectId, 'issue', issueId),
    enabled: !!projectId && !!issueId,
  });

  const linkedDocs = data?.documents ?? [];

  // Unlink mutation — find the relation on the doc side and delete it
  const unlinkMutation = useMutation({
    mutationFn: async (docId: string) => {
      // Get all links for the doc, find the one pointing to this issue
      const linksResp = await documentsApi.links.list(docId, projectId);
      const rel = linksResp.links.find(
        (l) => l.target_type === 'issue' && l.target_entity_id === issueId
      );
      if (rel) {
        await documentsApi.links.delete(docId, projectId, rel.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docs-by-entity', projectId, 'issue', issueId] });
    },
  });

  const handleUnlink = (docId: string) => {
    if (!confirm('Unlink this document from this issue?')) return;
    unlinkMutation.mutate(docId);
  };

  const handleDocClick = (doc: EditorDocument) => {
    // Navigate to the document reader
    // Use project short_id from the doc's project_id
    const sid = getProjectShortId(doc.project_id);
    navigate(wsSlug ? `/w/${wsSlug}/pr/${sid}/do/${doc.id}` : `/pr/${sid}/do/${doc.id}`);
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">Loading documentation...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with link button */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-900">
          Linked Documents ({linkedDocs.length})
        </h3>
        <button
          onClick={() => setShowBrowser(true)}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          + Link Document
        </button>
      </div>

      {/* Document list */}
      {linkedDocs.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm font-medium">No documents linked yet</p>
          <p className="text-xs text-gray-400 mt-1">Link existing documents from your project</p>
        </div>
      ) : (
        <div className="space-y-2">
          {linkedDocs.map((doc) => {
            const typeInfo = DOC_TYPE_LABELS[doc.doc_type] || DOC_TYPE_LABELS.document;
            const statusClass = STATUS_COLORS[doc.status] || STATUS_COLORS.draft;

            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <typeInfo.Icon size={16} className="text-gray-400 flex-shrink-0" />
                <button
                  onClick={() => handleDocClick(doc)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="text-sm font-medium text-gray-900 truncate">{doc.title}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-500">{typeInfo.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusClass}`}>
                      {doc.status.replace('_', ' ')}
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => handleUnlink(doc.id)}
                  disabled={unlinkMutation.isPending}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all"
                  title="Unlink document"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Documentation browser modal */}
      {showBrowser && (
        <DocumentationBrowser
          projectId={projectId}
          issueId={issueId}
          existingDocIds={linkedDocs.map((d) => d.id)}
          onClose={() => setShowBrowser(false)}
          onLinked={() => {
            queryClient.invalidateQueries({ queryKey: ['docs-by-entity', projectId, 'issue', issueId] });
            setShowBrowser(false);
          }}
        />
      )}
    </div>
  );
}
